import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Button,createTheme,Paper,Skeleton,TableCell,TableRow,ThemeProvider,Typography } from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { useQuery } from "@tanstack/react-query";
import MUIDataTable,{ MUIDataTableOptions } from "mui-datatables";
import { useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from '../../helpers/matomo';
import Teams from "../../service/Teams";
import { userType } from "../../types/managements/types";
import { Team } from "../../types/teams/types";
import ChangeTeamModal from "./Modal/ChangeTeamModal";

const equipeService = new Teams();

export default function ListaEquipes() {
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState<boolean>(false);
  const [equipe, setEquipe] = useState<Team | null>();

  function handleOpen() {
    setOpen(!open);
  }

  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['GET_TEAMS'],
    queryFn: async () => {
      try {
        const promise = toast.promise(async () => {
          const data = await AttToken();
          if (!data) throw new Error('Token não encontrado');
          if (data) {
            const response = await equipeService.getAllByCurrentUser(data);
            if (!response) throw new Error('Erro ao buscar equipes');
            return response;
          }
        }, {
          pending: t('listaEquipes.buscaEquipes'),
          error: t('listaEquipes.erroBuscaEquipe'),
          success: t('listaEquipes.sucessoBuscaEquipe'),
        }, { toastId: 'getTeams' }); // Adiciona um toastId para evitar toasts duplicados
        return promise;
      } catch (err) {
        throw new Error('Error fetching teams');
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  function openModal(id: string) {
    const team = data?.find((team: Team) => team.teamID === id);
    if (team) {
      setEquipe(team);
      handleOpen();
    }
  }

  const onLoading = isLoading || isFetching;

  useEffect(() => {
    if (isSuccess) trackEventMatomo('gestão de equipes', 'sucesso', 'carregamento', 'equipes carregadas');
    if (isError) trackEventMatomo('gestão de equipes', 'erro', 'carregamento', 'falha ao carregar equipes');
    if (isSuccess && !data?.length) {
      toast.info(t('listaEquipes.semEquipes'), { toastId: 'zero' });
    }
  }, [onLoading, isSuccess, isError, data, t]);


  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  const columns = [
    {
      name: "nome",
      label: t('name'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => (<Typography width={150} fontSize='small' sx={{ wordBreak: 'break-word' }}>{value}</Typography>)
      }
    },
    {
      name: "gerente",
      label: 'Criado por',
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
      }
    },
    {
      name: "dataCadastro",
      label: t('teamManage.registered.createdAt').replace(':', ''),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => (
          <Typography fontSize='small'>{new Date(value).toLocaleString('pt-BR')}</Typography>
        )
      }
    },
    {
      name: "status",
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: boolean) => (
          value
            ? <Typography color='success' fontWeight={700}>{t('active')}</Typography>
            : <Typography color='error' fontWeight={700}>{t('inactive')}</Typography>
        ),
      }
    },
    {
      name: "teamID",
      label: t('edit'),
      options: {
        filter: false,
        sort: false,
        sortThirdClickReset: false,
        customBodyRender: (value: string) => (
          <Button
            onClick={() => openModal(value)}
            variant='outlined'
            endIcon={<BorderColorIcon />}
          >
            {t('edit')}
          </Button>),
      }
    },
    {
      name: "usersByTeam",
      label: 'usuários',
      options: {
        display: false,
      }
    },
    {
      name: "users",
      label: 'usuários',
      options: {
        display: false,
      }
    }
  ];

  const getMuiTheme = () =>
    createTheme({
      components: {
        MuiTableHead: {
          styleOverrides: {
            root: {
              button: {
                textAlign: 'left',
                width: 'fit-content',
                padding: 1,
                justifyContent: 'flex-start'
              }
            }
          }
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              wordBreak: 'break-word',
              padding: '10px'
            }
          }
        }
      }
    });

  const tableOptions: MUIDataTableOptions = {
    expandableRows: true,
    viewColumns: false,
    expandableRowsHeader: false,
    expandableRowsOnClick: true,
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    print: false,
    download: false,
    filter: false,
    responsive: 'simple',
    isRowExpandable: () => { return true; },
    renderExpandableRow: (rowData) => {
      const usuarios = rowData[5];
      if (!rowData[6]) return (
        <TableRow>
          <TableCell colSpan={rowData?.length}>
            <Typography variant="body2" color="textSecondary" align="center">
              {t('listaEquipes.semUsuarioAssociado')}
            </Typography>
          </TableCell>
        </TableRow>
      );
      return (
        <TableCell colSpan={6}>
          <Typography variant="h6" gutterBottom component="div">
            {t('listaEquipes.usuariosAssociados')}
          </Typography>
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('name')}</TableCell>
                  <TableCell>e-mail</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(usuarios as unknown as userType[])?.map((user: userType) => (
                  <TableRow key={user.id}>
                    <TableCell >{user.nome ?? 'Nome não encontrado'}</TableCell>
                    <TableCell >{user.email ?? 'e-mail não encontrado'}</TableCell>
                    <TableCell>
                      {user.status
                        ? <Typography color='success' fontWeight={700}>{t('active')}</Typography>
                        : <Typography color='error' fontWeight={700}>{t('inactive')}</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </TableCell>
      );
    },
  };

  return (
    <>
      {open && <ChangeTeamModal setEquipe={setEquipe} equipe={equipe as Team} open={open} setOpen={handleOpen} />}
      {onLoading
        ? (<Skeleton variant='rounded' height={600} width='100%' />)
        :
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={t('teamManage.teamList.tableTitle')}
            data={data ?? []}
            columns={columns}
            options={{ ...tableOptions, ...translatedTextLabels }}
          />
        </ThemeProvider>}
    </>
  );
}
